import { useCallback, useMemo } from "react";

import { useUserContext } from "@/features/auth/contexts/useUserContext";
import { useSubscriberAuth } from "@/features/businessSubscription/hooks/useSubscriberAuth";
import { ErrorRetry } from "@/features/common/components/ErrorRetry";
import { Title } from "@/features/common/components/Title";
import { FilterSideBar } from "@/features/common/components/filter/FilterSideBar";
import { FilterStagger } from "@/features/common/components/filter/FilterStagger";
import { Pagination } from "@/features/common/components/pagination/Pagination";
import {
    useConfirmDialogContext,
    withConfirmDialog,
} from "@/features/common/contexts/useConfirmDialogContext";
import { usePaginationContext } from "@/features/common/contexts/usePaginationContext";
import { useBreakpoints } from "@/features/common/hooks/useBreakpoint";
import { EmptySearchResult } from "@/features/job/components/jobList/EmptySearchResult";
import { ModelCard } from "@/features/model/components/ModelCard";
import { ModelCardSkeleton } from "@/features/model/components/ModelCardSkeleton";
import { ModelListFilterSection } from "@/features/model/components/modeList/ModelListFilterSection";
import { modelListFilterList } from "@/features/model/const/modelListFilterList";
import {
  ModelOrderByWithRelationInput,
  ModelWhereInput,
  SortOrder,
  useModelsQuery,
} from "@/generated/graphql-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";

const {
  features: {
    model: { maxPublicPage },
  },
} = getPublicConfig();

const ModelList = withConfirmDialog(
  ({
    count,
    query,
  }: {
    count?: number;
    query: {
      where: ModelWhereInput;
      orderBy: ModelOrderByWithRelationInput;
    };
  }) => {
    const { currentBreakpoint } = useBreakpoints();
    const { currentPage, itemsPerPage, startPosition } = usePaginationContext();
    const { isSubscriber } = useSubscriberAuth();
    const { openConfirmDialog } = useConfirmDialogContext();
    const { user } = useUserContext();

    const {
      data: { models } = {},
      isLoading,
      error,
      refetch,
    } = useModelsQuery({
      where: { ...query?.where },
      orderBy: Object.keys(query?.orderBy)?.length
        ? query?.orderBy
        : [{ order: SortOrder.Desc }, { id: SortOrder.Desc }],
      take: itemsPerPage,
      skip: startPosition,
    });

    const filterList = useMemo(() => {
      if (!isSubscriber) return modelListFilterList;

      return modelListFilterList.filter(
        (filter) => !!filter.displayToSubscriber,
      );
    }, [isSubscriber]);

    const shouldBlur = useCallback(
      (index: number) => {
        // no blur if not ordering by ig follower count or is subcriber
        if (!query?.orderBy?.instagramFollowerCount || isSubscriber)
          return false;

        return (
          // first row
          (currentBreakpoint === "sm" && index > 1) ||
          (currentBreakpoint === "lg" && index > 2) ||
          (currentBreakpoint === "xl" && index > 3) ||
          // first page
          currentPage !== 1
        );
      },

      [currentBreakpoint, currentPage, query?.orderBy, isSubscriber],
    );

    const onPageChange = (
      page: number,
      defaultOnPageChange: (page: number) => void,
    ) => {
      if (!user && page > maxPublicPage)
        return openConfirmDialog({
          content: "請先登入/註冊，才能瀏覽更多頁面",
          hideActionButtons: true,
        });

      defaultOnPageChange(page);
    };

    return (
      <>
        <FilterSideBar filterList={filterList} className="hidden md:block" />
        <FilterStagger filterList={filterList} className="md:hidden" />

        <section className="col-span-3 mt-6 space-y-4 sm:space-y-6 lg:mt-0">
          <div className="flex items-center justify-between text-base">
            <div className="flex items-end space-x-2">
              <Title size="h2" className="font-normal text-gray-900">
                KOL
              </Title>
              <div className="text-gray-700">/ {count}位KOL</div>
            </div>
          </div>

          <ModelListFilterSection />

          <div className="grid grid-cols-2 justify-center gap-5 lg:grid-cols-3 xl:grid-cols-4">
            {!!isLoading &&
              Array.from(Array(12)).map((_, index) => (
                <ModelCardSkeleton key={index} />
              ))}
            {!isLoading && !!error && (
              <div className="col-span-full">
                <ErrorRetry refetch={refetch} error={error}/>
              </div>
            )}
            {!isLoading && !models?.length && !error && (
              <div className="col-span-full">
                <EmptySearchResult />
              </div>
            )}
            {models?.map((model, index) => (
              <ModelCard {...model} isBlurred={shouldBlur(index)} key={index} />
            ))}

            <Pagination
              className="col-span-full -mx-4 py-3"
              totalPages={Math.ceil((count ?? 1) / itemsPerPage)}
              preventDefaultOnChange
              onChange={onPageChange}
            />
          </div>
        </section>
      </>
    );
  },
);

export default ModelList;
